import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import api from "../api";

const COLUMNS = [
  { key: "name", label: "City", numeric: false },
  { key: "studio_count", label: "Studio", numeric: true },
  { key: "single_count", label: "Single", numeric: true },
  { key: "double_count", label: "Double", numeric: true },
  { key: "triple_count", label: "Triple+", numeric: true },
  { key: "total_count", label: "Total", numeric: true },
  { key: "latest_entry", label: "Latest Entry", numeric: false },
];

const formatNumber = (val) => (val != null ? Number(val).toLocaleString() : "-");

export default function CityTable() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState({ key: "name", dir: "asc" });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.get("/housing/city_table/");
        setRows(res.data);
        setError(null);
      } catch (err) {
        console.error(err);
        setError("Could not load city table data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const toggleSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  const visibleRows = useMemo(() => {
    const f = filter.trim().toLowerCase();
    let out = f
      ? rows.filter((r) => (r.name || "").toLowerCase().includes(f))
      : [...rows];

    const { key, dir } = sort;
    out.sort((a, b) => {
      let av = a[key];
      let bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return dir === "asc" ? av - bv : bv - av;
      }
      av = String(av);
      bv = String(bv);
      return dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return out;
  }, [rows, filter, sort]);

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-100 bg-opacity-80 z-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 border-b-4 border-gray-200"></div>
      </div>
    );
  }

  if (error) return <p className="p-6 text-red-600">{error}</p>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="p-4 max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Cities Overview</h1>
          <input
            type="text"
            placeholder="Filter by city…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm"
          />
        </div>

        <div className="bg-white shadow-md p-4 rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border border-gray-300">
              <thead className="bg-gray-100">
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      className={`border px-2 py-1 cursor-pointer select-none ${
                        col.numeric ? "text-right" : "text-left"
                      }`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {sort.key === col.key && (
                          <span className="text-gray-400">
                            {sort.dir === "asc" ? "▲" : "▼"}
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/cities/${r.id}`)}
                    className="text-center hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="border px-2 py-1 text-left font-medium">
                      {r.name}
                    </td>
                    <td className="border px-2 py-1 text-right">
                      {formatNumber(r.studio_count)}
                    </td>
                    <td className="border px-2 py-1 text-right">
                      {formatNumber(r.single_count)}
                    </td>
                    <td className="border px-2 py-1 text-right">
                      {formatNumber(r.double_count)}
                    </td>
                    <td className="border px-2 py-1 text-right">
                      {formatNumber(r.triple_count)}
                    </td>
                    <td className="border px-2 py-1 text-right font-medium">
                      {formatNumber(r.total_count)}
                    </td>
                    <td className="border px-2 py-1 text-right">
                      {r.latest_entry
                        ? new Date(r.latest_entry).toLocaleDateString()
                        : "-"}
                    </td>
                  </tr>
                ))}
                {visibleRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={COLUMNS.length}
                      className="border px-2 py-6 text-center text-gray-400"
                    >
                      No cities match.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}